import { Component} from '@angular/core';
import { FileAuthorization } from '../../models/dataloadmanagement.model';
import { ClientSelectionService } from '../clients/client-selection.service';
import { DataloadmanagementService } from '../../services/dataloadmanagement.service';
import { takeUntil, distinctUntilChanged, finalize } from 'rxjs';
import { MatDialog } from '@angular/material/dialog';
import { EditfileauthorizationComponent, EditFileAuthorizationData, EditFileAuthorizationResult } from './editfileauthorization/editfileauthorization.component';
import { formatHttpErrorMessage } from '../../shared/utils/error.utils';
import { BaseTableComponent } from '../../shared/base/base-table.component';
import { resolveLastUpdatedBy } from '../../shared/utils/user.utils';

@Component({
  selector: 'app-dataloadmanagement',
  standalone: false,
  templateUrl: './dataloadmanagement.component.html',
  styleUrl: './dataloadmanagement.component.css',
})
export class DataloadmanagementComponent extends BaseTableComponent<FileAuthorization> {
  readonly displayedColumns: string[] = ['authorizationNumber', 'ftpDirectoryName', 'loadToleranceDays', 'autoProcessFiles', 'lastUpdated', 'lastUpdatedBy', 'comment', 'actions'];

  isLoading = false;
  clientId?: number;
  clientName?: string;
  override sortBy = 'authorizationNumber';
  override sortDir: 'asc' | 'desc' = 'asc';

  constructor(
    private dataloadManagementService: DataloadmanagementService,
    private clientSelection: ClientSelectionService,
    private dialog: MatDialog
  ) {
    super();
  }

  override ngOnInit(): void {
    super.ngOnInit();
    this.resolveClientDetails();
  }

  resolveClientDetails(): void {
    this.clientSelection.selectedClient$
      .pipe(
        distinctUntilChanged(),
        takeUntil(this.destroy$)
      )
      .subscribe((client) => {
        this.clientId = client?.ClientID ?? undefined;
        this.clientName = client?.DisplayName ?? undefined;
        if (this.clientId != null) {
          this.pageIndex = 0;
          this.loadDataWrapper();
        } else {
          this.dataSource.data = [];
          this.totalCount = 0;
          this.errorMessage = 'Please select a client to view data load management.';
        }
      });
  }

  openEditDialog(row: FileAuthorization): void {
    if (!this.clientId) return;

    const dialogData: EditFileAuthorizationData = {
      fileAuthorization: row,
      clientName: this.clientName
    };

    const dialogRef = this.dialog.open<EditfileauthorizationComponent, EditFileAuthorizationData, EditFileAuthorizationResult>(
      EditfileauthorizationComponent,
      {
        width: '500px',
        data: dialogData,
        autoFocus: false,
        restoreFocus: false,
      }
    );

    dialogRef.componentInstance.submitRequest.subscribe(result => {
      const lastUpdatedBy = resolveLastUpdatedBy();
      const request = {
        ClientID: this.clientId!,
        FileAuthorization: {
          ClientId: row.ClientId,
          AuthorizationNumber: row.AuthorizationNumber,
          FTPDirectoryName: row.FTPDirectoryName,
          LoadToleranceDays: result.loadToleranceDays,
          AutoProcessFiles: result.autoProcessFiles,
          LastUpdated: row.LastUpdated,
          LastUpdatedBy: lastUpdatedBy,
          Comment: result.comment
        }
      };

      this.isLoading = true;
      this.dataloadManagementService
        .updateFileAuthorization(request)
        .pipe(finalize(() => { this.isLoading = false; }))
        .subscribe({
          next: (res) => {
            if (res?.success) {
              dialogRef.close();
              this.loadDataWrapper();
            } else {
              dialogRef.componentInstance.dialogError = 'Update failed';
            }
          },
          error: (err) => {
            dialogRef.componentInstance.dialogError = formatHttpErrorMessage('Update failed', err);
          },
        });
    });
  }

  protected override loadData(skip: number, count: number): void {
    if (!this.clientId) return;
    this.isLoading = true;
    this.errorMessage = '';
    
    this.dataloadManagementService
      .getFileAuthorizations(this.clientId, this.sortBy, this.sortDir, skip, count)
      .pipe(finalize(() => { this.isLoading = false; }))
      .subscribe({
        next: (resp) => {
          this.dataSource.data = resp.fileAuthorizations ?? [];
          this.totalCount = resp.totalCount ?? this.dataSource.data.length;
        },
        error: (err) => {
          this.errorMessage = formatHttpErrorMessage(err, 'Failed to load file authorizations.');
          this.dataSource.data = [];
          this.totalCount = 0;
        },
      });
  }
}
